import type { ComponentPropsWithRef, ElementType, ReactNode } from "react"

type ObservedElementProps<T extends ElementType> = {
  readonly as?: T
  readonly children: ReactNode
  readonly elementId: string
  readonly content: string
  readonly type?: string
  readonly position?: string
} & Omit<
  ComponentPropsWithRef<T>,
  "as" | "children" | "elementId" | "content" | "type"
>

/**
 * Polymorphic wrapper component that marks an element for observation tracking.
 *
 * Renders a container element with `data-obs-*` attributes that are detected
 * by the observation boundary system. Use this inside an `<ObservedBoundary>`.
 *
 * ## Required attributes
 *
 * - `elementId` - Unique identifier for this element within the boundary
 * - `content` - Content identifier sent to analytics (often the same as elementId)
 *
 * ## Optional attributes
 *
 * - `type` - Element type category (defaults to "article")
 * - `as` - HTML element to render (defaults to "div")
 *
 * @example
 *   <ObservedBoundary boundaryId="list-1" boundaryType="article-list">
 *     <ObservedElement elementId="article-123" content="123">
 *       <ArticleTeaser />
 *     </ObservedElement>
 *   </ObservedBoundary>
 */
export function ObservedElement<T extends ElementType = "div">({
  as,
  children,
  elementId,
  content,
  position,
  type = "article",
  ...rest
}: ObservedElementProps<T>) {
  const Component = as ?? "div"

  return (
    <Component
      // biome-ignore lint: allow passthrough props here
      {...rest}
      data-k5a-pos={position}
      data-obs-element
      data-obs-element-content={content}
      data-obs-element-id={elementId}
      data-obs-element-type={type}
    >
      {children}
    </Component>
  )
}
